// TB-123: the clinic time zone and time display mode (admin setting,
// transactions-api GET settings/locale) plus the one date-time module of the
// portal. Contract: evidence/2026-10-22/customer-feedback/locale-contract.md.
//
// Appointment startTime/endTime and slot date/time are clinic WALL time sent
// as "<wall>Z". A wall value is kept as a moment.utc() whose UTC fields are
// the clinic clock. "Now" and "today" are always the clinic clock from the
// real time + clinicTimeZone, never the device clock relabelled.
// Display follows the mode: 'clinic' shows wall time as is, 'device' converts
// to the viewer's zone. The viewer can override the admin mode in Settings.
import { reactive } from 'vue'
import axios from 'axios'
import moment from 'moment'

export const DEFAULT_ZONE = 'Asia/Bahrain'
const DEFAULT = { clinicTimeZone: DEFAULT_ZONE, timeDisplayMode: 'clinic' }
const MODES = ['clinic', 'device']
const OVERRIDE_KEY = 'tabibbi.timeDisplayMode'

export const ISO = 'YYYY-MM-DD'
// Week starts Sunday; Friday and Saturday are the weekend.
export const WEEK_START = 0
export const WEEKEND_DAYS = [5, 6]

// Named display formats; views pick one of these instead of writing their own.
export const FORMATS = {
  iso: ISO,
  time: 'h:mm A',
  time2: 'hh:mm A',
  date: 'ddd, D MMM YYYY',
  dateTime: 'ddd, D MMM YYYY · h:mm A',
  dayMonth: 'D MMM',
  dayMonthYear: 'D MMM YYYY',
  weekday: 'dddd D MMMM',
  weekdayYear: 'dddd D MMMM YYYY',
  long: 'dddd, D MMMM YYYY',
  medium: 'll',
  mediumTime: 'll, h:mm A',
  monthDay: 'MMM DD, YYYY',
  monthDayTime: 'MMM DD, YYYY h:mm A',
  monthYear: 'MMMM YYYY',
  dayShort: 'ddd D MMM',
  monthD: 'MMM D',
  dYear: 'D, YYYY',
  monthDYear: 'MMM D, YYYY',
  dmy: 'DD/MM/YYYY',
  dmyTime: 'DD/MM/YYYY h:mm A'
}

function validZone (tz) {
  if (!tz || typeof tz !== 'string') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(0)
    return true
  } catch (e) {
    return false
  }
}

function readOverride () {
  try {
    const v = window.localStorage.getItem(OVERRIDE_KEY)
    return MODES.includes(v) ? v : ''
  } catch (e) {
    return ''
  }
}

export const locale = reactive({ ...DEFAULT, override: readOverride(), loaded: false })

function apply (d) {
  const tz = d && d.clinicTimeZone
  const mode = d && d.timeDisplayMode
  locale.clinicTimeZone = validZone(tz) ? tz : DEFAULT.clinicTimeZone
  locale.timeDisplayMode = MODES.includes(mode) ? mode : DEFAULT.timeDisplayMode
}

let pending = null

/** Fetches GET settings/locale once; `force` re-reads it (after login). Defaults when the call fails. */
export function loadLocale ({ force = false } = {}) {
  if (pending && !force) return pending
  const base = process.env.VUE_APP_TRANSACTIONS_SERVICE_API || ''
  pending = axios.get(`${base}settings/locale`)
    .then((r) => {
      const body = r && r.data
      apply(body && body.data && typeof body.data === 'object' ? body.data : body)
    })
    .catch(() => { if (!locale.loaded) apply(DEFAULT) })
    .then(() => { locale.loaded = true; return locale })
  return pending
}

/** Viewer's choice: 'clinic', 'device', or '' to follow the admin setting. */
export function setTimeModeOverride (mode) {
  const v = MODES.includes(mode) ? mode : ''
  locale.override = v
  try {
    if (v) window.localStorage.setItem(OVERRIDE_KEY, v)
    else window.localStorage.removeItem(OVERRIDE_KEY)
  } catch (e) {}
}

export const timeMode = () => locale.override || locale.timeDisplayMode
export const clinicZone = () => locale.clinicTimeZone || DEFAULT_ZONE

export function deviceZone () {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch (e) {
    return ''
  }
}

/** Short place name of a zone: "Asia/Bahrain" -> "Bahrain". */
export function zoneLabel (tz = clinicZone()) {
  const last = String(tz || '').split('/').pop()
  return last.replace(/_/g, ' ')
}

// ------------------------------------------------------------ zone maths
const dtfCache = {}
function partsIn (ms, tz) {
  const f = dtfCache[tz] || (dtfCache[tz] = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }))
  const p = {}
  f.formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value })
  return p
}

/** Minutes the zone is ahead of UTC at the instant `ms`. */
export function zoneOffset (ms, tz = clinicZone()) {
  try {
    const p = partsIn(ms, tz)
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second)
    return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000)
  } catch (e) {
    return 0
  }
}

const deviceOffset = (ms) => -new Date(ms).getTimezoneOffset()

const pad = (n) => String(n).padStart(2, '0')

/** "YYYY-MM-DD" of a wall date (date string, wall ISO, moment, or a local Date from a picker). */
export function wallDate (value) {
  if (!value) return ''
  if (moment.isMoment(value)) return value.isValid() ? value.format(ISO) : ''
  if (value instanceof Date) return isNaN(value) ? '' : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const m = moment(new Date(s))
  return m.isValid() ? m.format(ISO) : ''
}

/** "HH:mm" of a wall time ("9:30", "09:30:00", "9:30 AM", wall ISO, moment, or a local Date). */
export function wallTime (value) {
  if (value === null || value === undefined || value === '') return ''
  if (moment.isMoment(value)) return value.isValid() ? value.format('HH:mm') : ''
  if (value instanceof Date) return isNaN(value) ? '' : `${pad(value.getHours())}:${pad(value.getMinutes())}`
  const s = String(value).trim()
  const iso = /T(\d{2}):(\d{2})/.exec(s)
  if (iso) return `${iso[1]}:${iso[2]}`
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/.exec(s)
  if (!m) {
    const t = moment(s, ['h:mm A', 'hh:mm A', 'HH:mm', 'LT'], true)
    return t.isValid() ? t.format('HH:mm') : ''
  }
  let h = Number(m[1])
  const min = Number(m[2])
  if (m[3]) {
    const pm = m[3].toLowerCase() === 'pm'
    if (h === 12) h = pm ? 12 : 0
    else if (pm) h += 12
  }
  if (h > 23 || min > 59) return ''
  return `${pad(h)}:${pad(min)}`
}

// Wall value -> ms whose UTC fields are the wall clock (NaN when unreadable).
function wallMs (value, time) {
  if (time !== undefined && time !== null && time !== '') {
    const d = wallDate(value)
    const t = wallTime(time)
    if (!d || !t) return NaN
    return Date.parse(`${d}T${t}:00Z`)
  }
  if (value === null || value === undefined || value === '') return NaN
  if (moment.isMoment(value)) return value.isValid() ? Date.UTC(value.year(), value.month(), value.date(), value.hours(), value.minutes(), value.seconds()) : NaN
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return Date.parse(`${s}T00:00:00Z`)
  return moment.utc(s).valueOf()
}

/** Wall clock as a moment.utc() (the scale appointment times are read on). */
export function wall (value, time) {
  return moment.utc(wallMs(value, time))
}

/** The clinic clock now, on the wall scale (moment.utc whose fields are clinic time). */
export function clinicNow (ms = Date.now()) {
  return moment.utc(ms + zoneOffset(ms) * 60000)
}

/** Clinic date today, "YYYY-MM-DD". */
export const clinicToday = () => clinicNow().format(ISO)

/** Minutes since midnight on the clinic clock. */
export function clinicNowMin (ms) {
  const n = clinicNow(ms)
  return n.hours() * 60 + n.minutes()
}

/** The real instant (Date) of a clinic wall time: wallToInstant('2026-10-22', '10:00') or wallToInstant(wallIso). */
export function wallToInstant (value, time) {
  const w = wallMs(value, time)
  if (!Number.isFinite(w)) return new Date(NaN)
  const tz = clinicZone()
  const first = w - zoneOffset(w, tz) * 60000
  return new Date(w - zoneOffset(first, tz) * 60000)
}

/** Clinic wall moment of a real instant. */
export function instantToWall (value) {
  const ms = value instanceof Date ? value.getTime() : moment(value).valueOf()
  return Number.isFinite(ms) ? clinicNow(ms) : moment.invalid()
}

// True when device mode shows a different clock than clinic mode at `ms`.
function converts (ms, mode) {
  return mode === 'device' && deviceOffset(ms) !== zoneOffset(ms)
}

const fmtOf = (f) => FORMATS[f] || f || FORMATS.dateTime
const hasTime = (f) => /[hHm]/.test(f.replace(/\[[^\]]*\]/g, ''))

/**
 * Formats a clinic wall value (appointment/slot time) for the viewer.
 * fmt: a FORMATS key or a moment format. opts.label adds the zone where a
 * time stands alone (detail and booking views): "10:00 AM Bahrain time", or in
 * device mode "11:00 AM your time (10:00 AM Bahrain)". opts.mode forces a mode.
 */
export function formatWall (value, fmt = 'dateTime', { label = false, mode, fallback = '' } = {}) {
  const w = wallMs(value)
  if (!Number.isFinite(w)) return fallback
  const f = fmtOf(fmt)
  const clinic = moment.utc(w)
  const m = mode || timeMode()
  const instant = wallToInstant(w).getTime()
  if (!converts(instant, m)) {
    const out = clinic.format(f)
    return label && hasTime(f) ? `${out} ${zoneLabel()} time` : out
  }
  const local = moment(instant)
  const out = local.format(f)
  if (!label || !hasTime(f)) return out
  const sameDay = local.format(ISO) === clinic.format(ISO)
  return `${out} your time (${clinic.format(sameDay ? FORMATS.time : f)} ${zoneLabel()})`
}

/** Wall start-end range: "9:00 AM – 9:30 AM" (start only when the end is missing or not after it). */
export function formatRange (start, end, fmt = 'time', opts = {}) {
  const s = formatWall(start, fmt, { ...opts, label: false })
  const a = wallMs(start)
  const b = wallMs(end)
  if (!s || !Number.isFinite(b) || b <= a) return opts.label ? formatWall(start, fmt, opts) : s
  const e = formatWall(end, fmt, { ...opts, label: false })
  const sep = opts.sep || ' – '
  const range = `${s}${sep}${e}`
  if (!opts.label) return range
  const m = opts.mode || timeMode()
  const instant = wallToInstant(a).getTime()
  if (!converts(instant, m)) return `${range} ${zoneLabel()} time`
  return `${range} your time (${formatWall(start, fmt, { mode: 'clinic' })}${sep}${formatWall(end, fmt, { mode: 'clinic' })} ${zoneLabel()})`
}

/** Formats a real instant (createdAt, payments, chat) in the viewer's zone for the mode. */
export function formatInstant (value, fmt = 'dateTime', { label = false, mode, fallback = '' } = {}) {
  if (value === null || value === undefined || value === '') return fallback
  const ms = value instanceof Date ? value.getTime() : moment(value).valueOf()
  if (!Number.isFinite(ms)) return fallback
  const f = fmtOf(fmt)
  const m = mode || timeMode()
  if (converts(ms, m)) return moment(ms).format(f)
  const out = clinicNow(ms).format(f)
  return label && hasTime(f) && deviceOffset(ms) !== zoneOffset(ms) ? `${out} ${zoneLabel()} time` : out
}

/** "YYYY-MM-DD" a real instant falls on for the viewer (mode-aware), for grouping by day. */
export const instantDay = (value, opts) => formatInstant(value, ISO, opts)

/** Today for display grouping (mode-aware). Past/upcoming checks use clinicToday(). */
export function viewToday () {
  return formatInstant(Date.now(), ISO)
}

/** Body fields providers-api expects for a slot: { consultationDate: 'YYYY-MM-DD', startTime: 'HH:mm', endTime: 'HH:mm' }. */
export function toWallPayload ({ date, start, end }) {
  const out = { consultationDate: wallDate(date), startTime: wallTime(start) }
  if (end !== undefined) out.endTime = wallTime(end)
  return out
}

/** Sunday of the week containing `date` ("YYYY-MM-DD"). */
export function weekStart (date) {
  const m = moment(date, ISO)
  return m.clone().subtract((m.day() - WEEK_START + 7) % 7, 'day').format(ISO)
}

export function isWeekendDay (date) {
  return WEEKEND_DAYS.includes(moment(date, ISO).day())
}

/** Label of a calendar date "YYYY-MM-DD" (no time, no zone): formatDay('2026-10-22', 'weekday'). */
export function formatDay (date, fmt = 'date') {
  const m = moment(String(date || '').slice(0, 10), ISO, true)
  return m.isValid() ? m.format(fmtOf(fmt)) : ''
}

/** "HH:mm" -> "9:30 AM" (a clock time with no date, shown as is). */
export function formatClock (hhmm, fmt = 'time') {
  const t = wallTime(hhmm)
  return t ? moment.utc(`2000-01-01T${t}:00Z`).format(fmtOf(fmt)) : ''
}
